import { useEffect, useEffectEvent, useState } from 'react';

import { DotLottie, DotLottieReact } from '@lottiefiles/dotlottie-react';

import { cn } from '@/utils/tailwind';

import checkmarkRevealUrl from '@/icons/TranslatingCheck/checkmark_reveal.lottie?url';

interface TranslatingCheckProps {
  /** Increments on every translate start — restarts the reveal. */
  playToken: number;
  className?: string;
}

/**
 * Always-visible checkmark Lottie. Idle on the last frame; restarts the reveal
 * whenever `playToken` bumps. Holds the last frame when the clip finishes.
 *
 * Note: `@lottiefiles/dotlottie-react` drops the `style` prop when `className`
 * is set on DotLottieReact — size the wrapper and omit className on the player.
 */
const TranslatingCheck = ({ playToken, className }: TranslatingCheckProps) => {
  const [dotLottie, setDotLottie] = useState<DotLottie | null>(null);

  const showLastFrame = useEffectEvent(() => {
    if (!dotLottie || dotLottie.totalFrames <= 0) {
      return;
    }
    dotLottie.pause();
    dotLottie.setFrame(dotLottie.totalFrames - 1);
    // Ensure the canvas paints after seeking while paused.
    dotLottie.resize();
  });

  const playReveal = useEffectEvent(() => {
    if (!dotLottie) {
      return;
    }
    dotLottie.setFrame(0);
    dotLottie.resize();
    dotLottie.play();
  });

  useEffect(() => {
    if (!dotLottie) {
      return;
    }

    const settleOnLastFrame = () => {
      showLastFrame();
    };

    const onComplete = () => {
      showLastFrame();
    };

    dotLottie.addEventListener('load', settleOnLastFrame);
    dotLottie.addEventListener('complete', onComplete);

    if (dotLottie.isLoaded) {
      settleOnLastFrame();
    }

    return () => {
      dotLottie.removeEventListener('load', settleOnLastFrame);
      dotLottie.removeEventListener('complete', onComplete);
    };
  }, [dotLottie]);

  useEffect(() => {
    if (!dotLottie || playToken === 0) {
      return;
    }
    playReveal();
  }, [dotLottie, playToken]);

  return (
    <span
      className={cn(
        'pointer-events-none inline-flex size-4 shrink-0 items-center justify-center',
        className,
      )}
      data-testid="TranslatingCheck"
    >
      {/*
        Do not pass className into DotLottieReact — it skips applying width/height
        styles when className is set, which leaves the canvas invisible.
      */}
      <DotLottieReact
        src={checkmarkRevealUrl}
        autoplay={false}
        loop={false}
        dotLottieRefCallback={setDotLottie}
        style={{ width: '100%', height: '100%' }}
        width={16}
        height={16}
      />
    </span>
  );
};

export default TranslatingCheck;
